import {
	ContextMenu,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useOpen,
} from "@dgs/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../../../contactFilter";
import { useContactFilter } from "../../../contactFilter/useContactFilter";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";
import { useContactView } from "../../../shared/hooks/useContactView";
import { IContactIndexResource } from "../../../shared/types";
import { ContactUpdateInquiryContactIndexResource } from "../../../shared/types/contactUpdateInquiry";
import { ContactUpdateDiffDrawer } from "./contactUpdate/ContactUpdateDiffDrawer";

interface Props {
	contactUpdateInquiryId: Id;
}

export const OpenUpdateInquiryContactList: FC<Props> = ({ contactUpdateInquiryId }) => {
	const { open, handleOpen, handleClose } = useOpen();
	const [contactId, setContactId] = useState<Id | null>();
	const { t } = useTranslation();
	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const { onApplyContactFilter, openContactFilterDrawer, activeContactFilterId, applyFilterDrawerProps } =
		useContactFilter(contactUpdateInquiryService.contactsWithOpenUpdateInquiryKey(contactUpdateInquiryId));

	const queryKey = useMemo(() => {
		const baseKey = [
			...contactUpdateInquiryService.contactsWithOpenUpdateInquiryKey(contactUpdateInquiryId),
			contactUpdateInquiryService.contactsWithInquiryKeySuffix,
			`${activeContactFilterId}`,
		];

		if (currentContactView?.id) {
			return baseKey.concat(`${currentContactView.id}`);
		}

		return baseKey;
	}, [activeContactFilterId, contactUpdateInquiryId, currentContactView]);

	const handleClosed = () => {
		setContactId(null);
	};

	const dataListColumns: IDataListColumn<IContactIndexResource>[] = useMemo(
		() =>
			columns.concat({
				heading: "",
				type: "actions",
				size: "max-content",
				action: (contact) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							icon="edit"
							action={() => {
								setContactId(contact.id);
								handleOpen();
							}}
							label={t("View changes")}
							title={t("View changes")}
							shortcut="primary"
						/>
						<ContextMenuLinkItem
							icon="eye"
							to={`/contacts/active/${contact.id}`}
							label={t("View contact")}
							title={t("View contact")}
							shortcut={true}
						/>
					</ContextMenu>
				),
			}),
		[columns, handleOpen, t],
	);

	const initialSortingAttribute =
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="updateInquiries"
				empty={t("No inquiries available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Update inquiries")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>) =>
						contactUpdateInquiryService.contactsWithOpenUpdateInquiry(
							config,
							activeContactFilterId,
							currentContactView?.id || null,
							contactUpdateInquiryId,
						),
				)}
				headerActions={
					<>
						<LocalContactViewSwitcher />
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				onRowClick={({ id }) => {
					setContactId(id);
					handleOpen();
				}}
				enableBulkEdit={false}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			{contactId && (
				<ContactUpdateDiffDrawer
					open={open}
					onClose={handleClose}
					inquiryId={contactUpdateInquiryId}
					contactId={contactId}
					onClosed={handleClosed}
				/>
			)}
		</>
	);
};
